import { useEffect, useState, useSyncExternalStore } from 'react'
import { useLanguage } from '@/contexts/LanguageContext'
import { fetchMaps, type MapInfo } from '@/lib/maps'

// Katalog yüklendikçe doldurulan etiket önbelleği. getMapLabel() senkron
// çağrıldığı için (render sırasında) burada tutuluyor; katalog gelmeden önce
// harita adının baş harfi büyütülerek gösterilir.
//
// ⚠️ O geri düşüş bir *tahmindir*: slug ile etiket birbirinden bağımsız
// (`zesty` → "Zestafona"), dolayısıyla katalog gelmeden basılan ad yanlış
// olabilir. Bu yüzden katalog çözüldüğünde aboneler uyarılır ve `useMapLabel()`
// kullanan ekran yeniden çizilir — yoksa yanlış ad ekranda kalıcı olur.
const labelCache = new Map<string, string>()
const subscribers = new Set<() => void>()
let catalogVersion = 0

fetchMaps()
  .then(maps => maps.forEach(m => labelCache.set(m.name, m.label)))
  .catch(() => { /* etiketler ada geri düşer */ })
  .finally(() => {
    catalogVersion++
    subscribers.forEach(fn => fn())
  })

function subscribeToCatalog(fn: () => void): () => void {
  subscribers.add(fn)
  return () => { subscribers.delete(fn) }
}

export function getMapLabel(name: string): string {
  const cached = labelCache.get(name)
  if (cached) return cached
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : name
}

/**
 * getMapLabel'ın reaktif hâli: katalog sonradan geldiğinde bileşeni yeniden
 * çizer. Kendi başına katalog çekmeyen ekranlarda (StrategyMap,
 * StrategicPointsMap) bunu kullan — orada düz getMapLabel() ilk render'daki
 * tahmini ekranda dondurur.
 */
export function useMapLabel(name: string): string {
  useSyncExternalStore(subscribeToCatalog, () => catalogVersion)
  return getMapLabel(name)
}

interface Props {
  value: string
  onChange: (mapName: string) => void
}

export default function MapSelector({ value, onChange }: Props) {
  const { T } = useLanguage()
  const [maps, setMaps] = useState<MapInfo[]>([])

  useEffect(() => {
    fetchMaps().then(setMaps).catch(() => {})
  }, [])

  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="max-w-48 cursor-pointer rounded-sm border border-white/10 bg-white/5 px-2 py-1.5 text-xs text-foreground focus:border-primary focus:outline-none"
    >
      <option value="">{T.strategy.selectMap}</option>
      {maps.map(m => (
        <option key={m.name} value={m.name}>{m.label}</option>
      ))}
    </select>
  )
}
